import { batch } from '@preact/signals-core'
import { useSignal } from '@preact/signals-react'
import { useSignals } from '@preact/signals-react/runtime'
import r2wc from '@r2wc/react-to-web-component'
import { toCustomPropertiesString } from 'object-to-css-variables'
import { useEffect, useRef } from 'react'
import { SearchIcon } from './components/Icons/Icons'
import { EVENT_SEARCH_BUTTON_CLICK } from './const'
import type { ThemeConfig } from './defaultConfig'
import { searchbuttonConfig as defaultConfig } from './defaultConfig'
import * as $ from './signal/searchbutton'
import styles from './styles/searchbutton.module.css'
import type { ColorScheme } from './types'

export interface RegisterSearchButtonParams {
  colorScheme?: string
  themeConfig?: ThemeConfig
  label?: string
  container?: string
  className?: string
  fullWidth?: boolean
  onClick?: () => void
  keyboardNavigationHints?: boolean
}

const checkParams = (params: RegisterSearchButtonParams) => {
  // it's a web component, ignore
  if (!params || params.container) return

  batch(() => {
    // we don't wanna check for ! because in that case if someone doesn't pass it it will set it to false
    if (params.keyboardNavigationHints === false) {
      $.keyboardNavigationHints.value = params.keyboardNavigationHints
    }
    params.colorScheme && params.colorScheme !== 'system' && ($.colorScheme.value = params.colorScheme as ColorScheme)
    params.colorScheme &&
      params.colorScheme === 'system' &&
      ($.colorScheme.value = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    params.themeConfig &&
      ($.themeConfig.value = {
        '--font-primary': params.themeConfig['--font-primary'] ?? defaultConfig.ui.theme['--font-primary'],
        light: {
          ...defaultConfig.ui.theme.light,
          ...params.themeConfig.light
        },
        dark: {
          ...defaultConfig.ui.theme.dark,
          ...params.themeConfig.dark
        },
        system: {}
      })
    params.label && ($.buttonLabel.value = params.label)
    params.className && ($.className.value = params.className)
    params.fullWidth && ($.fullWidth.value = params.fullWidth)
    params.onClick && ($.onClick.value = params.onClick)
  })
}

export function SearchButton(props: RegisterSearchButtonParams) {
  useSignals()

  useEffect(() => {
    checkParams(props)
  }, [props])

  const colorScheme = useSignal($.colorScheme).value
  const buttonRef = useRef<HTMLButtonElement>(null)

  const themeStyle = toCustomPropertiesString($.themeConfig.value[colorScheme.value])

  const style = (
    <style>
      {`
      @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:ital,wght@0,400..700;1,400..700&display=swap');
      :root {
        ${themeStyle}
        --font-primary: "Instrument Sans", sans-serif;;
        font-size: 100%;
      }
    `}
    </style>
  )

  const buttonClasses = [
    styles.searchbutton,
    $.className.value,
    $.fullWidth.value ? styles['searchbutton--fullWidth'] : ''
  ].join(' ')

  function handleClick() {
    const event = new CustomEvent(EVENT_SEARCH_BUTTON_CLICK, {
      bubbles: true,
      detail: {}
    })
    document.dispatchEvent(event)
    $.onClick?.value?.()
  }

  // focus the button when the orama-wc-searchbox:close
  useEffect(() => {
    window.addEventListener('orama-wc-searchbox:close', () => {
      buttonRef.current?.focus()
    })
  }, [])

  return (
    <div className={styles.searchbutton_container}>
      {style}
      <button type="button" className={buttonClasses} onClick={handleClick} ref={buttonRef}>
        <SearchIcon className={styles.searchbutton__icon} width="16" height="16" />

        <span className={styles.searchbutton__label}>{$.buttonLabel.value}</span>
        {$.keyboardNavigationHints.value ? (
          <span className={styles.searchbutton__keys}>
            <kbd>⌘</kbd>
            <kbd>K</kbd>
          </span>
        ) : null}
      </button>
    </div>
  )
}

export function RegisterSearchButton(params: RegisterSearchButtonParams) {
  checkParams(params)
  if (!customElements.get('orama-search-button')) {
    return customElements.define('orama-search-button', r2wc(SearchButton))
  }
}
